import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Card } from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import { toaster } from '@/components/ui/toaster';
import { getCycle, updateCycle, CyclePayload } from '@/api/cyclesService';
import { queryKeys } from '@/lib/queryKeys';
import CycleSettingsForm from './CycleSettingsForm';
import SessionsBuilder from './SessionsBuilder';
import CycleUsageTab from './CycleUsageTab';

type Tab = 'settings' | 'sessions' | 'usage';

const TABS: { value: Tab; label: string }[] = [
  { value: 'settings', label: 'Réglages' },
  { value: 'sessions', label: 'Séances' },
  { value: 'usage', label: 'Utilisation' },
];

const CycleDetailPage = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('settings');

  const { data: cycle, isLoading } = useQuery({
    queryKey: queryKeys.pv2Cycle(id),
    queryFn: () => getCycle(id),
    enabled: Boolean(id),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: CyclePayload) => updateCycle(id, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.pv2Cycle(id), updated);
      queryClient.invalidateQueries({ queryKey: ['admin', 'pv2', 'cycles'], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: queryKeys.pv2CycleKpis, refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: queryKeys.pv2CycleSessions(id), refetchType: 'all' });
      toaster.create({ title: 'Cycle mis à jour', type: 'success' });
    },
  });

  if (isLoading) return <p className="text-sm text-gray-500">Chargement…</p>;
  if (!cycle) return <p className="text-sm text-gray-500">Cycle introuvable.</p>;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate('/cycles')}
            className="mb-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-brand-600 dark:hover:text-brand-300"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Dashboard / Plans / Cycles
          </button>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-900 dark:text-white">
            {cycle.name}
            {cycle.isDraft && <Badge color="yellow">Brouillon</Badge>}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{cycle.identifier}</p>
        </div>
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-gray-300 p-0.5 dark:border-gray-700">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={clsx(
              'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
              tab === t.value
                ? 'bg-brand-500 text-white'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'settings' && (
        <Card>
          <CycleSettingsForm
            initial={cycle}
            isSaving={updateMutation.isPending}
            onSubmit={(payload) => updateMutation.mutateAsync(payload)}
            onCancel={() => navigate('/cycles')}
          />
        </Card>
      )}

      {tab === 'sessions' && <SessionsBuilder cycle={cycle} />}

      {tab === 'usage' && <CycleUsageTab cycleId={id} />}
    </>
  );
};

export default CycleDetailPage;
